import { ResolveFn } from "@angular/router";
import { inject } from "@angular/core";
import { IUser } from "@app/shared/users/users/interfaces/user.interface";
import { map } from "rxjs";
import { AuthService } from "@app/shared/auth/services/auth.service";

/**
 * Route resolver that fetches the current authenticated user's profile data.
 *
 * This resolver is used to pre-load the authenticated user's profile information
 * before navigating to profile-related pages. It refreshes the authentication data
 * and returns the current user's profile, ensuring the user data is up-to-date.
 *
 * @example
 * ```typescript
 * {
 *   path: 'my-profile',
 *   component: MyProfileComponent,
 *   resolve: {
 *     user: getMyProfileResolver
 *   }
 * }
 * ```
 *
 * @param _route - The current activated route snapshot (unused in this resolver).
 * @param _state - The router state (unused).
 * @returns An observable that emits the resolved user profile data.
 * @throws {Error} When the user is not found after refreshing auth data.
 * @since 1.0.0
 */
export const getMyProfileResolver: ResolveFn<IUser> = (_, _state) => {
  const authService = inject(AuthService);

  // Refresh authentication data and return the current user's profile
  return authService.refreshAuthData().pipe(
    map(() => {
      const user = authService.getPlainUser();
      if (!user) {
        throw new Error("User not found");
      }
      return user;
    })
  );
};
